/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import moment from 'moment';
import classNames from 'classnames';
import React from 'react';
import SortableTable from '#components/support/table/SortableTable/index.jsx';
import { dateSortingFn } from '#components/support/table/SortableTable/sortingFunctions.js';
import LinkCell from './LinkCell/index.jsx';
import {
  colName,
  colStatus,
  colLastUpdated,
  colDeactivation,
  colModelTypes,
} from './ViewOrganizationsTable.module.scss';

const updatedAtCell = info => {
  const updatedAt = info.getValue();
  return moment(updatedAt).format('M/D/YYYY');
};

const deactivationCell = info => {
  const deactivationDate = info.getValue();
  return deactivationDate ? moment(deactivationDate).format('M/D/YYYY') : null;
};

const useColumns = () =>
  React.useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Organization',
        cell: info => <LinkCell value={info.getValue()} row={info.row.original} />,
        meta: {
          className: colName,
          headerClassName: colName,
        },
      },
      {
        id: 'status',
        accessorKey: 'active',
        header: 'Status',
        cell: info => {
          const active = info.getValue();
          return <span>{active ? 'Active' : 'Inactive'}</span>;
        },
        meta: {
          className: colStatus,
          headerClassName: colStatus,
        },
      },
      {
        id: 'lastUpdated',
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        cell: updatedAtCell,
        sortingFn: dateSortingFn,
        meta: {
          className: classNames('text-right', colLastUpdated),
          headerClassName: colLastUpdated,
        },
      },
      {
        id: 'deactivationDate',
        accessorKey: 'deactivationDate',
        header: 'Deactivation',
        cell: deactivationCell,
        sortingFn: dateSortingFn,
        meta: {
          className: classNames('text-right', colDeactivation),
          headerClassName: colDeactivation,
        },
      },
      {
        id: 'modelTypes',
        accessorKey: 'permissions.modelTypes',
        header: 'Model Types',
        cell: info => {
          const modelTypes = info.getValue();
          return <span>{modelTypes.join(', ')}</span>;
        },
        meta: {
          className: colModelTypes,
          headerClassName: colModelTypes,
        },
      },
    ],
    []
  );

type Organization = {
  uuid: string,
  status: boolean,
  name: string,
  deactivationDate: string,
  lastUpdated: string,
  modelTypes: string,
};

type Props = {|
  data: Array<Organization>,
|};

const ViewOrganizationsTable = (props: Props): Element<typeof SortableTable> => {
  const { data } = props;
  const columns = useColumns();

  return (
    <SortableTable
      data={data}
      columns={columns}
      initialSorting={[{ id: 'name', desc: false }]}
      className="organizations-table"
      tableId="orgs-table"
      fixedHeight="900px"
    />
  );
};

export default ViewOrganizationsTable;
